import { useEffect, useState } from "react";
import { api, mutate, errorMessage } from "../lib/api";
import { formatCHF, formatDate } from "../lib/format";
type Account = {
  balance_rappen: number;
  entries: { id: string; amountRappen: number; reason: string; createdAt: number }[];
};
export function CreditAccount({ tenantId, memberId }: { tenantId: string; memberId: string }) {
  const [account, setAccount] = useState<Account | null>(null),
    [amount, setAmount] = useState(""),
    [reason, setReason] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const path = `/tenants/${tenantId}/members/${memberId}/credit`;
  useEffect(() => {
    let active = true;
    api<Account>(path)
      .then((a) => {
        if (active) setAccount(a);
      })
      .catch((e) => {
        if (active) setError(errorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [path]);
  async function adjust() {
    setBusy(true);
    try {
      const a = await mutate<Account>(path, {
        amount_rappen: Math.round(Number(amount) * 100),
        reason,
      });
      setAccount(a);
      setAmount("");
      setReason("");
      setError("");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="my-5 rounded-xl border border-slate-200 p-4">
      <summary className="cursor-pointer font-medium">
        Guthaben {account ? formatCHF(account.balance_rappen) : "…"}
      </summary>
      <div className="mt-4 space-y-3">
        <p className="text-sm text-slate-500">
          Guthaben wird erst bei Freigabe einer Rechnung angerechnet. Jede Korrektur bleibt im
          Verlauf sichtbar.
        </p>
        <label className="block text-sm">
          Korrektur CHF (negativ zum Abziehen)
          <input
            type="number"
            step="0.01"
            className="input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          Begründung
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
        <button
          className="btn-sm"
          disabled={busy || !reason.trim() || !Number(amount)}
          onClick={adjust}
        >
          Korrektur buchen
        </button>
        {error && (
          <p role="alert" className="text-red-700">
            {error}
          </p>
        )}
        {account?.entries.map((e) => (
          <div key={e.id} className="flex justify-between gap-4 border-t py-2 text-sm">
            <span>
              {formatDate(e.createdAt)} · {e.reason}
            </span>
            <span>{formatCHF(e.amountRappen)}</span>
          </div>
        ))}
      </div>
    </details>
  );
}
export function DraftCredit({
  tenantId,
  invoiceId,
  memberId,
  requested,
  onSaved,
}: {
  tenantId: string;
  invoiceId: string;
  memberId: string;
  requested: number;
  onSaved: () => void;
}) {
  const [account, setAccount] = useState<Account | null>(null),
    [amount, setAmount] = useState(String(requested / 100)),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    api<Account>(`/tenants/${tenantId}/members/${memberId}/credit`)
      .then((a) => {
        if (active) setAccount(a);
      })
      .catch((e) => {
        if (active) setError(errorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [tenantId, memberId]);
  return (
    <div className="my-4 rounded-xl border p-4">
      <p className="font-medium">
        Guthaben anrechnen {account && `(verfügbar ${formatCHF(account.balance_rappen)})`}
      </p>
      <p className="my-2 text-sm text-slate-500">
        Die Auswahl wird bei Freigabe erneut geprüft. Der Rechnungsbetrag und die MwSt bleiben
        unverändert.
      </p>
      <div className="flex gap-2">
        <input
          aria-label="Guthaben CHF"
          className="input"
          type="number"
          step="0.01"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <button
          className="btn-sm"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await mutate(`/tenants/${tenantId}/invoices/${invoiceId}/credit`, {
                amount_rappen: Math.round(Number(amount) * 100),
              });
              setError("");
              onSaved();
            } catch (e) {
              setError(errorMessage(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Auswahl speichern
        </button>
      </div>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
